/**
 * lib/reconFixes.ts
 * ------------------------------------------------------------------
 * FINANCE — turning a Pastel-vs-portal difference into something the
 * bursar can DO about it.
 *
 * lib/pastelAgeAnalysis says where the two ledgers disagree. This says,
 * for each disagreement, which fixes fit — so the Pastel Check screen can
 * show a card with buttons instead of a row to puzzle over:
 *
 *   linkCode      the pupil is the Pastel customer, the code just was never
 *                 recorded on them (exact name match, or a likely one)
 *   linkPupil     the same, for a pupil the names do not find: chosen from
 *                 a search of every pupil without a code (Oct 2026)
 *   addPupil      Pastel has the account and the portal has nobody: add
 *                 them, as Pastel carries them — a pending pupil (a former
 *                 one, for an account with nothing this year) with
 *                 Pastel's balance as the opening balance (Oct 2026)
 *   useName       the code matches but Pastel spells the name differently
 *   voidInvoice   the portal carries an invoice Pastel never charged — the
 *                 classic case is a family that paid the year up front
 *   recordPayment the portal owes more by an amount no single invoice
 *                 explains: most likely a payment Pastel has and the
 *                 portal does not. With the Detailed Ledger (Oct 2026)
 *                 it is offered once per Pastel receipt the portal lacks,
 *                 already carrying that receipt's amount, date and RC
 *                 number
 *   raiseInvoice  Pastel owes more: a charge the portal has not raised
 *                 (every June AS pupil was exactly P36,000 short on
 *                 7 Oct 2026 — the June-intake fee)
 *   reversePayment a portal payment Pastel has no receipt for — entered
 *                 twice, or against the wrong pupil. Offered beside
 *                 "keep it, Pastel to capture it" (an acknowledge with the
 *                 note written): which is true is for whoever entered it
 *   acknowledge   nothing to change here; noted, with a reason, so the
 *                 card stops coming back
 *
 * Every fix is a SUGGESTION with its amount worked out; the person
 * confirms it on the card. Nothing here writes.
 * ------------------------------------------------------------------ */
import type { DiffRow } from '@/lib/pastelAgeAnalysis';
import { chargesMissingFromPastel, matchMoney, reversedStillInPastel, type LedgerLine } from '@/lib/pastelLedger';
import { isOpeningBalance, type Invoice } from '@/lib/financeCore';

/**
 * Why an invoice may NOT be voided, or null when it may. Nothing in the
 * portal voided an invoice before Oct 2026, so the door is narrow: never
 * one with money paid or a credit on it (undo those first, through their
 * own screens), never an opening balance brought forward from Pastel.
 */
export function voidRefusal(inv: Pick<Invoice, 'status' | 'kind' | 'invoiceNo' | 'paid' | 'adjusted'>): string | null {
  if (inv.status === 'void') return 'It is already void.';
  if (isOpeningBalance(inv)) return 'It is an opening balance brought forward from Pastel — correct it with a credit note instead.';
  if ((Number(inv.paid) || 0) > 0) return 'Money has been paid on it — reverse the payment first (Payments).';
  if ((Number(inv.adjusted) || 0) > 0) return 'It carries a credit note — reverse that first (Credit Notes).';
  return null;
}

export type FixKind = 'linkCode' | 'linkPupil' | 'addPupil' | 'useName' | 'voidInvoice' | 'recordPayment' | 'raiseInvoice' | 'reversePayment' | 'acknowledge';

export interface Fix {
  kind: FixKind;
  /** Button text. */
  label: string;
  /** One line on what it will do. */
  detail: string;
  studentId?: string;
  code?: string;
  name?: string;
  invoiceId?: string;
  amount?: number;
  /** recordPayment from a Pastel receipt: its date (YYYY-MM-DD) and RC number. */
  date?: string;
  reference?: string;
  /** reversePayment: the portal payment. */
  paymentId?: string;
  /** acknowledge: the note, already written. */
  note?: string;
  /** addPupil: what they are added as. */
  status?: 'pending' | 'left';
  /** The fix the evidence points to most. */
  recommended?: boolean;
}

export interface ReconInvoice {
  id: string;
  studentId: string;
  invoiceNo?: string;
  kind?: string;
  total: number;
  paid?: number;
  adjusted?: number;
  status?: string;
  term?: string;
  issuedAt?: string;
  year?: string;
}

export interface ReconStudent {
  id: string;
  name: string;
  pastelCode?: string;
  className?: string;
  /** 'left' for a former pupil — the card says so, and voiding is offered. */
  status?: string;
}

/**
 * A warning for the card, when the two systems disagree about whether the
 * pupil is still at the school: marked left in the portal while Pastel still
 * lists them in a class category, e.g. the eight former "Alumni class"
 * pupils Pastel carried in Form 2 and 3 on 7 Oct 2026.
 */
export function leftFlag(row: DiffRow, students: ReconStudent[]): string | null {
  const st = row.studentId ? students.find((s) => s.id === row.studentId) : undefined;
  if (!st || st.status !== 'left') return null;
  if (row.kind === 'missing_in_pastel') return 'Marked as left in the portal.';
  const inClass = row.category && !/gone|old student|alumni|left/i.test(row.category);
  return inClass
    ? `Marked as left in the portal, but Pastel still lists them under “${row.category}”. Check whether they really left.`
    : 'Marked as left in the portal.';
}

const CENT = 0.005;
const r2 = (n: number) => Math.round(n * 100) / 100;
const pula = (n: number) => `P${Math.abs(n).toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function words(s: string): Set<string> {
  return new Set(s.toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/).filter((t) => t.length > 1));
}

/** How alike two names are: shared words, and whether the shorter is wholly in the longer. */
export function nameLikeness(a: string, b: string): { shared: number; contained: boolean } {
  const x = words(a), y = words(b);
  const shared = [...x].filter((t) => y.has(t)).length;
  return { shared, contained: shared > 0 && shared === Math.min(x.size, y.size) };
}

/** Same words, any order — "BAHUMA BOLENG IPELENG" and "Bahuma Boleng Ipeleng". */
export function sameName(a: string, b: string): boolean {
  const x = words(a), y = words(b);
  return x.size === y.size && [...x].every((t) => y.has(t));
}

/**
 * Pupils who might be this Pastel customer: no Pastel code yet, and at least
 * two words of the name in common (one name wholly inside the other ranks
 * first). "LABANE JESSICA" finds "Labane Jessica Maele".
 */
export function likelyPupils(pastelName: string, students: ReconStudent[], limit = 3): ReconStudent[] {
  return students
    .filter((s) => !s.pastelCode)
    .map((s) => ({ s, l: nameLikeness(pastelName, s.name) }))
    .filter(({ l }) => l.shared >= 2)
    .sort((p, q) => Number(q.l.contained) - Number(p.l.contained) || q.l.shared - p.l.shared)
    .slice(0, limit)
    .map(({ s }) => s);
}

function outstanding(i: ReconInvoice): number {
  return r2((Number(i.total) || 0) - (Number(i.paid) || 0) - (Number(i.adjusted) || 0));
}

/** A portal payment, as the Pastel Check needs to name it. */
export interface PortalPayment {
  id: string;
  receiptNo?: string;
  amount: number;
  receivedAt: string;
  recordedByName?: string;
}

/** A payment reversed in the portal whose receipt Pastel still holds. */
export interface ReversedInPortal {
  payment: PortalPayment & { reversedByName?: string; reversedReason?: string; reversedAt?: string };
  receipt: LedgerLine;
}

/**
 * What the Detailed Ledger shows about one pupil (lib/pastelLedger).
 * The last three are entries SOMEONE made in the portal that Pastel does
 * not carry — the screen flags them and names who, so nothing has to be
 * sent round as a separate list.
 */
export interface LedgerEvidence {
  /** Pastel receipts the portal has no payment for. */
  missingReceipts?: LedgerLine[];
  /** Portal payments Pastel has no receipt for. */
  portalOnlyPayments?: PortalPayment[];
  /** Invoices raised by hand in the portal that Pastel never charged. */
  portalOnlyInvoices?: ReconInvoice[];
  /** Payments reversed in the portal that Pastel still holds. */
  reversedInPortal?: ReversedInPortal[];
  /** The account's entries this year — none means an old account. */
  activity?: number;
}

/** Raised by hand — Manual Invoice (`…_x…`) or the Pastel Check (`…_r…`) — not by a billing run. */
export function isHandRaised(invoiceId: string): boolean {
  return /_(x|r)[0-9a-z]+$/.test(invoiceId);
}

/**
 * What the Detailed Ledger shows about one pupil, worked out the one way
 * both the Pastel Check and seed/align-to-pastel.ts use. `invoices` are the
 * pupil's live (non-void) invoices; `payments` the money that really
 * arrived (neither reversed nor a reversal); `reversedPayments` those
 * someone reversed. Portal entries are only looked for when the balances
 * differ: where they agree, nothing is wrong whatever the entries say.
 * `until` is the last day the Pastel file covers: a portal entry made
 * after it cannot be in the file, so it is never called missing from it.
 */
export function ledgerEvidence<P extends PortalPayment>(input: {
  difference: number;
  lines: LedgerLine[];
  since: string;
  invoices: ReconInvoice[];
  payments: P[];
  reversedPayments: P[];
  until?: string;
}): LedgerEvidence & { portalOnlyPayments: P[] } {
  const money = matchMoney(input.lines, input.payments, input.since);
  if (Math.abs(input.difference) < 0.5) return { missingReceipts: money.missingFromPortal, portalOnlyPayments: [] };
  const covered = (iso?: string) => !input.until || String(iso ?? '').slice(0, 10) <= input.until;
  const handRaised = input.invoices.filter((i) => isHandRaised(i.id) && !isOpeningBalance({ kind: i.kind as Invoice['kind'], invoiceNo: i.invoiceNo ?? '' })
    && String(i.issuedAt ?? '').slice(0, 10) > input.since && covered(i.issuedAt));
  return {
    missingReceipts: money.missingFromPortal,
    portalOnlyPayments: money.missingFromPastel.filter((p) => covered(p.receivedAt)),
    portalOnlyInvoices: chargesMissingFromPastel(input.lines, handRaised.map((i) => ({ ...i, total: Number(i.total) || 0 }))),
    reversedInPortal: reversedStillInPastel(money.missingFromPortal, input.reversedPayments),
  };
}

const shortDate = (iso: string) => new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** One line per portal entry Pastel does not carry, for the card's red banner. */
export function portalFlags(ev: LedgerEvidence): string[] {
  return [
    ...(ev.portalOnlyPayments ?? []).map((p) => `${p.receiptNo ?? 'Payment'} · ${pula(p.amount)} · ${shortDate(p.receivedAt)}${p.recordedByName ? ` · entered by ${p.recordedByName}` : ''} — no such receipt in Pastel`),
    ...(ev.portalOnlyInvoices ?? []).map((i) => `${i.invoiceNo ?? 'Invoice'} · ${pula(Number(i.total) || 0)}${i.issuedAt ? ` · ${shortDate(i.issuedAt)}` : ''} · raised by hand — Pastel never charged it`),
    ...(ev.reversedInPortal ?? []).map(({ payment: p, receipt: l }) => `${p.receiptNo ?? 'Payment'} · ${pula(p.amount)} reversed in the portal${p.reversedByName ? ` by ${p.reversedByName}` : ''}${p.reversedAt ? ` on ${shortDate(p.reversedAt)}` : ''}${p.reversedReason ? ` (“${p.reversedReason}”)` : ''} — Pastel still holds it as ${l.ref}`),
  ];
}

/**
 * The fixes that fit one row of the comparison. `invoices` are the pupil's
 * live (non-void) invoices; `students` is every pupil, for name matching;
 * `ev` is what the Detailed Ledger shows, when the upload was one.
 */
export function fixesFor(row: DiffRow, invoices: ReconInvoice[], students: ReconStudent[], ev: LedgerEvidence = {}): Fix[] {
  const missingReceipts = ev.missingReceipts ?? [];
  const portalOnly = ev.portalOnlyPayments ?? [];
  const portalInvoices = ev.portalOnlyInvoices ?? [];
  const reversed = ev.reversedInPortal ?? [];
  // With the ledger, a gap no Pastel receipt explains is a charge, not a lost payment.
  const fromLedger = ev.missingReceipts !== undefined;
  const flagged = portalOnly.length + portalInvoices.length + reversed.length > 0;
  const fixes: Fix[] = [];
  const ack: Fix = { kind: 'acknowledge', label: 'Note and hide', detail: 'Nothing to change in the portal — record why, and stop showing this.' };

  if (row.kind === 'missing_in_portal') {
    for (const s of likelyPupils(row.pastelName, students)) {
      fixes.push({
        kind: 'linkCode', label: `Link to ${s.name}`, studentId: s.id, code: row.code, name: s.name,
        detail: `Record ${row.code} on ${s.name}${s.className ? ` (${s.className})` : ''}. Their balance is then compared with Pastel's.`,
      });
    }
    if (fixes[0]) fixes[0].recommended = true;
    /* Pastel is the record: the account belongs in the portal. Either it is
       a pupil the names did not find — choose them — or nobody the portal
       knows, and they are added as Pastel carries them. An account with
       nothing this year is an old one: added as a former pupil, it does not
       sit in Admissions' pending list waiting to be enrolled. */
    fixes.push({
      kind: 'linkPupil', label: 'Link to a pupil…', code: row.code,
      detail: `Choose the pupil who is ${row.pastelName} in Pastel, and record ${row.code} on them.`,
    });
    const status = ev.activity === 0 ? 'left' : 'pending';
    const titled = row.pastelName.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
    fixes.push({
      kind: 'addPupil', label: status === 'left' ? 'Add as a former pupil' : 'Add to the portal', code: row.code, name: titled, amount: r2(row.pastel), status,
      detail: `${status === 'left' ? 'Add them as a former pupil' : 'Add them as a pending pupil (Admissions enrols them)'} with Pastel code ${row.code} and Pastel's balance of ${pula(row.pastel)} as their opening balance.`,
    });
    return [...fixes, ack];
  }

  if (row.kind === 'name_only' && row.studentId) {
    fixes.push({
      kind: 'linkCode', label: `Link ${row.code}`, studentId: row.studentId, code: row.code, recommended: true,
      detail: `${row.portalName} is ${row.pastelName} in Pastel. Record the code so the match is certain from now on.`,
    });
  }

  if (row.kind === 'missing_in_pastel' && row.studentId) {
    /* A pupil who has LEFT, owing in the portal and unknown to Pastel, is
       most often a pupil billed after they had gone — the 19 Sep 2026 run
       invoiced 43 pupils parked in the old Alumni class. If one unpaid
       invoice is the whole balance, voiding it is the likeliest fix. */
    const st = students.find((s) => s.id === row.studentId);
    const out: Fix[] = [];
    if (st?.status === 'left' && row.portal > 0) {
      const match = invoices.find((i) => i.status !== 'void' && i.kind !== 'opening'
        && !String(i.invoiceNo ?? '').startsWith('OPEN/') && !(Number(i.paid) > 0) && !(Number(i.adjusted) > 0)
        && Math.abs(outstanding(i) - row.portal) < CENT);
      if (match) {
        out.push({
          kind: 'voidInvoice', label: `Void ${match.invoiceNo ?? 'invoice'}`, studentId: row.studentId, invoiceId: match.id,
          amount: r2(Number(match.total) || 0), recommended: true,
          detail: `${st.name} has left and Pastel holds nothing for them; ${match.invoiceNo ?? 'this invoice'} (${pula(Number(match.total) || 0)}) is the whole portal balance. Void it if they were billed after leaving.`,
        });
      }
    }
    // Maybe Pastel has them under a code nobody linked — the other half of a missing_in_portal pair.
    return [...out, { ...ack, label: 'Not in Pastel — note it', detail: 'Pastel has no account for this pupil (or it is settled there). Open one in Pastel, or note why not.' }];
  }

  // Same code, different spelling.
  if (row.studentId && row.pastelName && row.portalName && !sameName(row.pastelName, row.portalName)
    && (row.kind === 'agree' || row.kind === 'differs')) {
    const titled = row.pastelName.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
    fixes.push({
      kind: 'useName', label: 'Use Pastel’s spelling', studentId: row.studentId, name: titled,
      detail: `Rename “${row.portalName}” to “${titled}” in the portal.`,
    });
  }

  const diff = row.difference; // portal − Pastel
  const live = !!row.studentId && Math.abs(diff) >= 0.5 && (row.kind === 'differs' || row.kind === 'name_only');
  const offeredVoid = new Set<string>();

  /* ENTRIES SOMEONE MADE IN THE PORTAL THAT PASTEL DOES NOT CARRY. Which side
     is right is for whoever made them, so both ways out are offered and
     neither is pressed on them: undo it in the portal, or keep it and note
     that Pastel is to follow. When these entries are the whole gap, nothing
     else is recommended over them. */
  let portalExplains = false;
  if (live && portalOnly.length) {
    portalExplains = Math.abs(r2(portalOnly.reduce((t, p) => t + (Number(p.amount) || 0), 0)) + diff) < CENT;
    for (const p of portalOnly) {
      const no = p.receiptNo ?? 'payment';
      fixes.push({
        kind: 'reversePayment', label: `Reverse ${no} · ${pula(p.amount)}`, studentId: row.studentId!, paymentId: p.id, amount: r2(Number(p.amount) || 0),
        detail: `${no} (${pula(p.amount)}, ${shortDate(p.receivedAt)}${p.recordedByName ? `, entered by ${p.recordedByName}` : ''}) has no receipt in Pastel. Reverse it if it was entered twice or against the wrong pupil.`,
      });
    }
    fixes.push({
      ...ack, label: 'Keep — Pastel to capture it', note: `${portalOnly.map((p) => p.receiptNo ?? pula(p.amount)).join(', ')} is genuine; Pastel has yet to capture it.`,
      detail: 'The payment is real and Pastel is missing it. Note that, and stop showing this until the balances move.',
    });
  }
  if (live && portalInvoices.length) {
    for (const i of portalInvoices) {
      if (voidRefusal({ status: i.status as Invoice['status'], kind: i.kind as Invoice['kind'], invoiceNo: i.invoiceNo ?? '', paid: Number(i.paid) || 0, adjusted: Number(i.adjusted) || 0 })) continue;
      offeredVoid.add(i.id);
      fixes.push({
        kind: 'voidInvoice', label: `Void ${i.invoiceNo ?? 'invoice'} · ${pula(Number(i.total) || 0)}`, studentId: row.studentId!, invoiceId: i.id, amount: r2(Number(i.total) || 0),
        detail: `${i.invoiceNo ?? 'This invoice'} was raised by hand in the portal and Pastel never charged it. Void it if it should not have been raised.`,
      });
    }
    fixes.push({
      ...ack, label: 'Keep — Pastel to raise it', note: `${portalInvoices.map((i) => i.invoiceNo ?? pula(Number(i.total) || 0)).join(', ')} is right; Pastel has yet to charge it.`,
      detail: 'The charge is right and Pastel is missing it. Note that, and stop showing this until the balances move.',
    });
  }
  if (live && reversed.length) {
    fixes.push({
      ...ack, label: 'Pastel to reverse it too', note: `${reversed.map(({ payment: p }) => p.receiptNo ?? pula(p.amount)).join(', ')} was rightly reversed; Pastel has yet to reverse ${reversed.map(({ receipt }) => receipt.ref).join(', ')}.`,
      detail: 'The reversal is right and Pastel still holds the receipt. Note that, and stop showing this until the balances move.',
    });
  }

  if (live) {
    if (diff > 0) {
      // Pastel receipts the portal has no payment for: the evidence itself.
      // When they add up to the difference they are the answer.
      const receiptsExplain = missingReceipts.length > 0
        && Math.abs(r2(missingReceipts.reduce((t, l) => t - l.amount, 0)) - diff) < CENT;
      // The portal says they owe more. An invoice that alone explains it is
      // the likeliest culprit — one Pastel never charged.
      const match = invoices.find((i) => i.status !== 'void' && i.kind !== 'opening'
        && (Math.abs(outstanding(i) - diff) < CENT || Math.abs((Number(i.total) || 0) - diff) < CENT));
      for (const l of missingReceipts) {
        const undone = reversed.find((x) => x.receipt === l);
        fixes.push({
          kind: 'recordPayment', label: `Record ${l.ref} · ${pula(-l.amount)} · ${shortDate(l.date)}`, studentId: row.studentId!,
          amount: r2(-l.amount), date: l.date, reference: l.ref, recommended: receiptsExplain && !undone,
          detail: undone
            ? `Pastel still holds ${l.ref} (${pula(-l.amount)}), which the portal reversed as ${undone.payment.receiptNo ?? 'a payment'}. Record it again if the reversal was a mistake.`
            : `Pastel received ${pula(-l.amount)} on ${shortDate(l.date)} (${l.ref}) and the portal has no payment for it. Record it with Pastel's date and reference.`,
        });
      }
      if (match && !(Number(match.paid) > 0) && !offeredVoid.has(match.id)) {
        fixes.push({
          kind: 'voidInvoice', label: `Void ${match.invoiceNo ?? 'invoice'}`, studentId: row.studentId!, invoiceId: match.id, amount: r2(Number(match.total) || 0),
          recommended: !receiptsExplain && !portalInvoices.length,
          detail: `${match.invoiceNo ?? 'This invoice'} (${pula(Number(match.total) || 0)}) is the whole difference and Pastel never charged it — e.g. the fees were paid up front. Voiding it brings the portal to Pastel's ${pula(row.pastel)}.`,
        });
      }
      if (!missingReceipts.length && !portalInvoices.length) {
        fixes.push({
          kind: 'recordPayment', label: `Record payment of ${pula(diff)}`, studentId: row.studentId!, amount: r2(diff),
          recommended: !match && !fromLedger,
          detail: `Pastel shows ${pula(diff)} more paid than the portal. Record the payment it holds (you can change the amount, date and reference).`,
        });
      }
    } else {
      fixes.push({
        kind: 'raiseInvoice', label: `Raise invoice for ${pula(-diff)}`, studentId: row.studentId!, amount: r2(-diff), recommended: !portalExplains,
        detail: `Pastel charges ${pula(-diff)} more than the portal. Raise the missing invoice (you choose what it is for).`,
      });
    }
  }

  // Someone's entry is in question: the choice is theirs, so none is pressed on them.
  if (flagged) for (const f of fixes) f.recommended = false;
  return fixes.length ? [...fixes, ack] : [];
}
